import { action, computed, observable } from 'mobx';
import BoardCollection from 'app/stores/boards/BoardCollection';
import BoardPanelTypes from 'app/stores/boards/boardPanelTypes';
import $decks from 'app/stores/decks/$decks';
import { omit, startCase } from 'lodash';
import deckTypes from '@kentik/ui-shared/boards/deckTypes';
import { getPanelLayout } from 'app/stores/boards/generateLayout';
import { isSmallScreen } from 'app/stores/boards/utils';
import { setBoardLabel } from '@kentik/ui-shared/boards/boards';

class BoardsStore {
  @observable.ref
  highlightedPanelIds = new Set();

  @observable
  isSmallScreen = false;

  collection = new BoardCollection();

  /** Unsaved personal boards (deck pages) */
  personalBoards = new BoardCollection();

  initialize() {
    this.isSmallScreen = isSmallScreen();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
    }

    if (this.store.$auth.hasPermission('ude', { overrideForSudo: false })) {
      setBoardLabel('Dashboard');
    }
  }

  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  @action
  handleResize = () => {
    const smallScreen = isSmallScreen();
    if (this.isSmallScreen !== smallScreen) {
      this.isSmallScreen = smallScreen;
    }
  };

  getPanelConfig(panel) {
    const { type } = panel.get ? panel.get() : panel;
    const { $auth } = this.store;
    if (type === 'ude' && !$auth.hasPermission('ude', { overrideForSudo: false })) {
      return null;
    }

    return BoardPanelTypes[type];
  }

  async getPersonalBoard(type, { canCustomize = true, fetch = true, boardId } = {}) {
    if (boardId) {
      if (fetch) {
        await this.collection.fetch();
      }

      const board = this.collection.find((b) => String(b.id) === String(boardId));
      if (board) {
        if (fetch) {
          await board.fetch();
        }
        return board;
      }
      // Board not found or inaccessible — fall through to default personal board
    }

    if (canCustomize) {
      if (fetch) {
        await this.collection.fetch();
      }

      const board = this.collection.find((b) => b.get('share_level') === 'self' && b.get('type') === type);

      if (board) {
        if (fetch) {
          // fetch board to get panels
          await board.fetch();
        }

        return board;
      }
    }

    let board = this.personalBoards.find((b) => b.isNew && b.get('type') === type);

    if (board) {
      return board;
    }

    const { layout, widgets } = $decks.deckFactories[type](this);

    board = this.personalBoards.build({
      title: deckTypes[type]?.label || `${startCase(type)} Page`,
      share_level: 'self',
      type,
      layout,
      config: {},
      panels: widgets.map((widget) => ({
        id: widget.id,
        type: 'widget',
        title: widget.config?.title || '',
        layout: getPanelLayout(layout, widget.id),
        config: {
          showTitle: true,
          isDefault: true,
          description: '',
          widgetName: widget.widgetName,
          ...omit(widget.config, ['title'])
        }
      }))
    });

    this.personalBoards.add(board);
    // personalBoards is used to keep track of unsaved generated boards, but change the collection so that it will be added correctly when saved
    board.collection = this.collection;
    return board;
  }

  /**
   * All boards loaded into the collection, as { value, label } select options.
   * Used by landing page selectors in General.js (user editing their own profile).
   */
  @computed
  get landingPageBoards() {
    return this.collection.get().map((model) => ({ value: String(model.id), label: model.get('title') }));
  }

  /**
   * Non-personal boards that can be assigned as another user's landing page.
   * Used anywhere a user needs to assign a Board to another user: UserInfo, Users, RoleSetLandingPage
   */
  @computed
  get assignableBoards() {
    return this.collection.get().filter((model) => model.get('share_level') !== 'self');
  }

  /**
   * @param {string} boardType
   * @param {WithOptional<BoardPanelJson, BoardPanelDefaults>} panelConfig
   * @param {{ boardId?: string | number }} [options]
   * @returns {Promise<import('./BoardModel').default>}
   */
  async addPanelToPersonalBoard(boardType, panelConfig, { boardId } = {}) {
    const board = await this.getPersonalBoard(boardType, { boardId });
    await board.addPanel(panelConfig);
    return board;
  }

  @action
  setHighlightedPanelIds(panelIds) {
    this.highlightedPanelIds = new Set(panelIds);
  }
}

export default new BoardsStore();
